/**
 * The countries Rigo Accounting sells to, and what each calls a company's tax ID (the design's 2.11; suite ADR-0101).
 * Nepal pays in NPR with VAT and its PAN is required; everywhere else pays in USD and the ID is optional. Which country
 * pays in which currency, and at what price, is RigoSub's; this is only how each is named and written.
 */
export interface SalesCountry {
  code: string;
  name: string;
  idLabel: string;
  idHint: string;
  required: boolean;
  /** The ID's digits, when the country has a fixed shape for it. */
  digits?: number;
}

export const SALES_COUNTRIES: SalesCountry[] = [
  {
    code: "NP",
    name: "Nepal",
    idLabel: "Company PAN",
    idHint: "9 digits",
    required: true,
    digits: 9,
  },
  {
    code: "US",
    name: "United States",
    idLabel: "EIN (optional)",
    idHint: "9 digits",
    required: false,
    digits: 9,
  },
  {
    code: "AU",
    name: "Australia",
    idLabel: "ABN (optional)",
    idHint: "11 digits",
    required: false,
    digits: 11,
  },
  {
    code: "NZ",
    name: "New Zealand",
    idLabel: "NZBN (optional)",
    idHint: "13 digits",
    required: false,
    digits: 13,
  },
  {
    code: "AE",
    name: "United Arab Emirates",
    idLabel: "TRN (optional)",
    idHint: "",
    required: false,
  },
  {
    code: "JP",
    name: "Japan",
    idLabel: "Corporate number (optional)",
    idHint: "13 digits",
    required: false,
    digits: 13,
  },
];

/** A country by its code, or Nepal's when it isn't one sold to. */
export function salesCountry(code: string | null | undefined): SalesCountry {
  return SALES_COUNTRIES.find((c) => c.code === code) ?? SALES_COUNTRIES[0]!;
}

/** Why a tax ID doesn't fit its country, or null when it does. */
export function taxIdProblem(code: string | null | undefined, taxId: string | null | undefined) {
  const country = salesCountry(code);
  const id = taxId?.trim() ?? "";
  if (!id) return country.required ? `Enter the ${country.idLabel}.` : null;
  if (country.digits && !new RegExp(`^\\d{${country.digits}}$`).test(id)) {
    return `${country.idLabel.replace(" (optional)", "")} is ${country.digits} digits.`;
  }
  return null;
}
